import { Fragment, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Box,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  REPORT_TYPES,
  REPORT_TYPE_LABELS,
  formatMoney,
} from '../utils/reportesData';

const EXPANDIBLE_TYPES = [
  REPORT_TYPES.COMPRAS,
  REPORT_TYPES.VENTAS,
  REPORT_TYPES.SALIDAS,
];

function isExpandible(reportType, productId) {
  return productId === 'TODOS' && EXPANDIBLE_TYPES.includes(reportType);
}

function MoneyCell({ value, bold = false }) {
  return (
    <TableCell align="right" sx={bold ? { fontWeight: 700 } : undefined}>
      Bs. {formatMoney(value)}
    </TableCell>
  );
}

function MaterialBreakdownRows({ materiales }) {
  if (!materiales?.length) {
    return (
      <TableRow>
        <TableCell colSpan={4}>
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Sin movimientos por material
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return materiales.map((row) => (
    <TableRow key={row.productoId}>
      <TableCell>{row.material}</TableCell>
      <TableCell align="right">{formatMoney(row.cantidad)}</TableCell>
      <MoneyCell value={row.total} />
      <MoneyCell value={row.precioPromedio} />
    </TableRow>
  ));
}

function MaterialBreakdownDetail({ periodo, colSpan, expanded }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ py: 1.5, px: 1, bgcolor: 'grey.50' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Peso (kg)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Total pagado
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Precio prom. / u
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <MaterialBreakdownRows materiales={periodo.materiales} />
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

function renderExpandiblePeriodRows(periodos, { expandedKey, onToggle, renderMainCells, colSpan }) {
  return periodos.flatMap((p) => {
    const expanded = expandedKey === p.key;
    return (
      <Fragment key={p.key}>
        <TableRow
          hover
          onClick={() => onToggle(p.key)}
          sx={{ cursor: 'pointer' }}
        >
          <TableCell padding="checkbox" sx={{ width: 48 }}>
            <IconButton size="small" aria-label={expanded ? 'Contraer' : 'Expandir'}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </TableCell>
          {renderMainCells(p)}
        </TableRow>
        <MaterialBreakdownDetail periodo={p} colSpan={colSpan} expanded={expanded} />
      </Fragment>
    );
  });
}

function renderComprasRows(periodos, expandProps) {
  const renderMainCells = (p) => (
    <>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.total} />
      <TableCell align="right">{formatMoney(p.cantidad)}</TableCell>
      <MoneyCell value={p.costoPromedio} />
    </>
  );

  if (expandProps) {
    return renderExpandiblePeriodRows(periodos, {
      ...expandProps,
      renderMainCells,
      colSpan: 5,
    });
  }

  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      {renderMainCells(p)}
    </TableRow>
  ));
}

function renderVentasRows(periodos, expandProps) {
  const renderMainCells = (p) => (
    <>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.total} />
      <TableCell align="right">{formatMoney(p.cantidad)}</TableCell>
      <MoneyCell value={p.precioPromedio} />
    </>
  );

  if (expandProps) {
    return renderExpandiblePeriodRows(periodos, {
      ...expandProps,
      renderMainCells,
      colSpan: 5,
    });
  }

  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      {renderMainCells(p)}
    </TableRow>
  ));
}

function renderSalidasRows(periodos, expandProps) {
  const renderMainCells = (p) => (
    <>
      <TableCell>{p.label}</TableCell>
      <TableCell align="right">{formatMoney(p.cantidad)}</TableCell>
      <MoneyCell value={p.valorEstimado} />
    </>
  );

  if (expandProps) {
    return renderExpandiblePeriodRows(periodos, {
      ...expandProps,
      renderMainCells,
      colSpan: 4,
    });
  }

  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      {renderMainCells(p)}
    </TableRow>
  ));
}

function renderGastosRows(periodos) {
  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.total} />
      <TableCell>
        {Object.entries(p.categorias || {})
          .map(([cat, monto]) => `${cat}: Bs. ${formatMoney(monto)}`)
          .join(' · ') || '—'}
      </TableCell>
    </TableRow>
  ));
}

function renderInventarioRows(periodos) {
  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      <TableCell>{p.label}</TableCell>
      <TableCell align="right">{formatMoney(p.stock)}</TableCell>
      <MoneyCell value={p.costoPromedio} />
      <MoneyCell value={p.valor} bold />
    </TableRow>
  ));
}

function renderUtilidadBrutaRows(periodos) {
  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.ventas} />
      <MoneyCell value={p.costos} />
      <MoneyCell value={p.utilidadBruta} bold />
    </TableRow>
  ));
}

function renderUtilidadOperativaRows(periodos) {
  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.utilidadBruta} />
      <MoneyCell value={p.gastos} />
      <MoneyCell value={p.utilidadOperativa} bold />
    </TableRow>
  ));
}

function renderFlujoRows(periodos) {
  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.ingresos} />
      <MoneyCell value={p.egresos} />
      <MoneyCell value={p.balance} bold />
    </TableRow>
  ));
}

function renderResumenRows(periodos) {
  return periodos.map((p) => (
    <TableRow key={p.key} hover>
      <TableCell>{p.label}</TableCell>
      <MoneyCell value={p.compras} />
      <MoneyCell value={p.ventas} />
      <MoneyCell value={p.gastos} />
      <MoneyCell value={p.utilidadBruta} />
      <MoneyCell value={p.utilidadOperativa} />
    </TableRow>
  ));
}

const TABLE_CONFIG = {
  [REPORT_TYPES.COMPRAS]: {
    headers: ['Período', 'Total compras', 'Cantidad', 'Costo promedio'],
    render: renderComprasRows,
    totals: (t, expandible) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        {expandible && <TableCell />}
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.total} bold />
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {formatMoney(t.cantidad)}
        </TableCell>
        <MoneyCell value={t.cantidad > 0 ? t.total / t.cantidad : 0} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.VENTAS]: {
    headers: ['Período', 'Total ventas', 'Cantidad', 'Precio promedio'],
    render: renderVentasRows,
    totals: (t, expandible) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        {expandible && <TableCell />}
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.total} bold />
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {formatMoney(t.cantidad)}
        </TableCell>
        <MoneyCell value={t.cantidad > 0 ? t.total / t.cantidad : 0} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.SALIDAS]: {
    headers: ['Período', 'Cantidad retirada', 'Valor estimado'],
    render: renderSalidasRows,
    totals: (t, expandible) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        {expandible && <TableCell />}
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {formatMoney(t.cantidad)}
        </TableCell>
        <MoneyCell value={t.valorEstimado} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.GASTOS]: {
    headers: ['Período', 'Total gastado', 'Por categoría'],
    render: renderGastosRows,
    totals: (t) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.total} bold />
        <TableCell />
      </TableRow>
    ),
  },
  [REPORT_TYPES.INVENTARIO]: {
    headers: ['Producto', 'Stock', 'Costo promedio', 'Valor'],
    render: renderInventarioRows,
    totals: (t) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {formatMoney(t.stock)}
        </TableCell>
        <TableCell />
        <MoneyCell value={t.valor} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.UTILIDAD_BRUTA]: {
    headers: ['Período', 'Ingresos (ventas)', 'Costos', 'Utilidad bruta'],
    render: renderUtilidadBrutaRows,
    totals: (t) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.ventas} bold />
        <MoneyCell value={t.costos} bold />
        <MoneyCell value={t.utilidadBruta} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.UTILIDAD_OPERATIVA]: {
    headers: ['Período', 'Utilidad bruta', 'Gastos', 'Utilidad operativa'],
    render: renderUtilidadOperativaRows,
    totals: (t) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.utilidadBruta} bold />
        <MoneyCell value={t.gastos} bold />
        <MoneyCell value={t.utilidadOperativa} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.FLUJO_CAJA]: {
    headers: ['Período', 'Ingresos', 'Egresos', 'Balance'],
    render: renderFlujoRows,
    totals: (t) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.ingresos} bold />
        <MoneyCell value={t.egresos} bold />
        <MoneyCell value={t.balance} bold />
      </TableRow>
    ),
  },
  [REPORT_TYPES.RESUMEN]: {
    headers: ['Período', 'Compras', 'Ventas', 'Gastos', 'Util. bruta', 'Util. operativa'],
    render: renderResumenRows,
    totals: (t) => (
      <TableRow sx={{ bgcolor: '#ECEFF1' }}>
        <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
        <MoneyCell value={t.compras} bold />
        <MoneyCell value={t.ventas} bold />
        <MoneyCell value={t.gastos} bold />
        <MoneyCell value={t.utilidadBruta} bold />
        <MoneyCell value={t.utilidadOperativa} bold />
      </TableRow>
    ),
  },
};

export default function ReporteTabla({
  reportType,
  productId = 'TODOS',
  periodos,
  totales,
  isProductSnapshot,
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const config = TABLE_CONFIG[reportType] || TABLE_CONFIG[REPORT_TYPES.VENTAS];
  const expandible = isExpandible(reportType, productId);

  const handleToggle = (key) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const expandProps = expandible
    ? { expandedKey, onToggle: handleToggle }
    : null;

  const headers = expandible ? ['', ...config.headers] : config.headers;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        {isProductSnapshot ? 'Detalle de inventario' : `Tabla · ${REPORT_TYPE_LABELS[reportType]}`}
      </Typography>

      {periodos.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No hay registros en el rango seleccionado.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                {headers.map((h, index) => (
                  <TableCell
                    key={`${h}-${index}`}
                    align={index === 0 && !expandible ? 'left' : index === 0 ? 'center' : 'right'}
                    sx={{ fontWeight: 700, width: index === 0 && expandible ? 48 : undefined }}
                    padding={index === 0 && expandible ? 'checkbox' : 'normal'}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {config.render(periodos, expandProps)}
              {config.totals(totales, expandible)}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
